/**
 * 납품계 API — backend /delivery/* 와 1:1.
 *
 * 분할납품요구서(분납서) PDF + 준공일 입력 → 5종 양식 PDF 생성.
 * 분납서·사진대지는 multipart 업로드.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

/** 분납서 자동 추출 결과 (POST /delivery/parse-bunap) */
export interface BunapFields {
  procurementNo: string | null;
  requestDate: string | null;   // 납품요구일자 → 계약/착수일
  endUser: string | null;       // 수요기관
  department: string | null;
  businessName: string | null;  // 사업명 → 공사명
  itemName: string | null;
  spec: string | null;
  identNo: string | null;
  unit: string | null;
  qty: number | null;
  totalAmount: number | null;   // 품대계
  deliveryDue: string | null;
}

export interface DeliveryListRow {
  id: number;
  docNo: string;
  issueDate: string;
  recipient: string;
  businessName: string;
  amount: number;
  hasPhoto: boolean;
  bunapFilename: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** 표지 붙임서류 항목 = 문서이름 + 제출부수 */
/** ⚠같은 이름의 타입이 `pages/documents/AttachmentListEditor.tsx` 에도 있다 — **둘을 같이 고칠 것** */
export interface DocAttachment {
  name: string;
  copies: string;
  /** 어느 장인지 가리키는 바뀌지 않는 표식 — 이 줄을 빼면 PDF 에서도 그 장이 빠진다 (v2.179.0) */
  key?: string;
  file?: File | null;              // 새로 첨부한 PDF(전송 시 문서 뒤 병합)
  existingFileId?: number | null;  // 서버에 저장된 첨부 id(수정 모드)
  existingFileName?: string | null;
}

/** 서버 저장 붙임 첨부 메타 (GET 상세의 extraFiles). */
export interface DocExtraFile {
  id: number;
  sort: number;
  name: string | null;
  filename: string | null;
}

/** 붙임 목록을 FormData 에 추가 — attachments jsonb(이름·부수만) + 항목별 첨부파일(extra_i) + 유지할 기존첨부 id(extraKeepIds). */
export function appendAttachmentsToForm(fd: FormData, attachments: DocAttachment[] | undefined | null): void {
  if (!attachments) return;
  // ⚠`key`(어느 장인지 가리키는 표식)도 같이 보낸다 — 빼면 「목록에서 뺀 장이 PDF 에 그대로 나온다」(v2.179.0)
  fd.append('attachments', JSON.stringify(attachments.map((a) => (
    a.key ? { name: a.name, copies: a.copies, key: a.key } : { name: a.name, copies: a.copies }
  ))));
  const keepIds: number[] = [];
  attachments.forEach((a, i) => {
    if (a.file) fd.append(`extra_${i}`, a.file);
    else if (a.existingFileId != null) keepIds.push(a.existingFileId);
  });
  fd.append('extraKeepIds', JSON.stringify(keepIds));
}

export interface DeliveryDetail {
  id: number;
  docNo: string;
  issueDate: string;
  recipient: string;
  department: string | null;
  businessName: string;
  procurementNo: string | null;
  contractDate: string | null;
  completionDate: string | null;
  deliveryDue: string | null;
  amount: number;
  itemName: string | null;
  spec: string | null;
  unit: string | null;
  qty: number;
  linkedSalesId: string | null;
  includeSeal: boolean;
  attachments: DocAttachment[] | null;
  extraFiles: DocExtraFile[];
  hasPhoto: boolean;
  bunapFilename: string | null;
  photoFilename: string | null;
  createdBy: string | null;
}

export interface DeliveryCreateInput {
  docNo?: string;
  issueDate: string;
  recipient: string;
  department?: string;
  businessName: string;
  procurementNo?: string;
  contractDate?: string;
  completionDate?: string; // '' → 준공년월일 공란
  deliveryDue?: string;
  amount: number;
  itemName?: string;
  spec?: string;
  unit?: string;
  qty?: number;
  includeSeal?: boolean; // 직인 표시 여부 (기본 true)
  attachments?: DocAttachment[]; // 붙임서류 목록 (미지정이면 기본 목록)
}

/** 납품계 수정 입력 — 모든 필드 선택(보낸 것만 갱신) */
export type DeliveryUpdateInput = Partial<DeliveryCreateInput>;

async function dataOf<T>(res: Response): Promise<T> {
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 분납서 PDF → 자동 추출 필드 */
export async function parseBunapFile(file: File): Promise<BunapFields> {
  const fd = new FormData();
  fd.append('bunap', file);
  const res = await fetch(`${BASE}/delivery/parse-bunap`, {
    method: 'POST', credentials: 'include', body: fd,
  });
  return dataOf<BunapFields>(res);
}

/** 준공일(시행일) 기준 다음 문서번호 제안 */
export async function getNextDocNo(date: string): Promise<{ docNo: string; seqNo: number }> {
  const res = await fetch(`${BASE}/delivery/next-doc-no?date=${encodeURIComponent(date)}`, {
    credentials: 'include',
  });
  return dataOf(res);
}

export async function listDeliveries(): Promise<DeliveryListRow[]> {
  const res = await fetch(`${BASE}/delivery`, { credentials: 'include' });
  return (await dataOf<{ items: DeliveryListRow[] }>(res)).items;
}

export async function getDelivery(id: number): Promise<DeliveryDetail> {
  const res = await fetch(`${BASE}/delivery/${id}`, { credentials: 'include' });
  return dataOf<DeliveryDetail>(res);
}

/** 납품계 작성 — 필드 + 분납서(필수) + 사진대지(선택) */
export async function createDelivery(
  input: DeliveryCreateInput,
  bunap: File,
  photo: File | null,
): Promise<{ id: number; docNo: string }> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || k === 'attachments') continue;
    fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
  }
  appendAttachmentsToForm(fd, input.attachments);
  fd.append('bunap', bunap);
  if (photo) fd.append('photo', photo);
  const res = await fetch(`${BASE}/delivery`, { method: 'POST', credentials: 'include', body: fd });
  return dataOf(res);
}

/** 사진대지 추가·교체 (나중 병합) */
export async function updateDeliveryPhoto(id: number, photo: File): Promise<void> {
  const fd = new FormData();
  fd.append('photo', photo);
  const res = await fetch(`${BASE}/delivery/${id}`, { method: 'PATCH', credentials: 'include', body: fd });
  await dataOf(res);
}

/** 납품계 수정 — 필드(보낸 것만 갱신) + (선택) 사진대지 교체 */
export async function updateDelivery(
  id: number,
  input: DeliveryUpdateInput,
  photo?: File | null,
): Promise<void> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || k === 'attachments') continue;
    fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
  }
  appendAttachmentsToForm(fd, input.attachments);
  if (photo) fd.append('photo', photo);
  const res = await fetch(`${BASE}/delivery/${id}`, { method: 'PATCH', credentials: 'include', body: fd });
  await dataOf(res);
}

export async function deleteDelivery(id: number): Promise<void> {
  const res = await fetch(`${BASE}/delivery/${id}`, { method: 'DELETE', credentials: 'include' });
  await dataOf(res);
}

export function deliveryPdfUrl(id: number): string {
  return `${BASE}/delivery/${id}/pdf`;
}
